import React, {
  PropsWithChildren,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Portal, Paper, Box } from '@mui/material';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import { Gripper } from 'spw-components/global/Gripper';
import GrabAreaGrayIcon from 'spw-static/images/grab-area-gray.svg';
import styled from 'styled-components';
import { colors } from 'spw-theme';
import { Resizable, ResizeCallback } from 're-resizable';

interface Props extends PropsWithChildren {
  anchorEl: Element;
  title: ReactNode;
  open: boolean;
  draggable?: boolean;
  dialogPaperStyle?: React.CSSProperties;
  defaultPosition?: { top: number; left: number };
  height?: number;
  width?: number;
  minHeight?: number;
  minWidth?: number;
  onStop?: (e: DraggableEvent, data: DraggableData) => void | false;
  enableConfig?: {
    left?: boolean;
    right?: boolean;
    topLeft?: boolean;
    topRight?: boolean;
    bottomLeft?: boolean;
    bottomRight?: boolean;
  };
}

const DialogPaper = styled(Paper)`
  .drag,
  .resize {
    background-color: ${colors.containerBorder};

    & > img {
      width: 16px;
    }
  }

  .drag {
    cursor: move;
  }
`;

const GrabArea = styled(Box)`
  && {
    .gripper {
      border-radius: 3px 3px 0 0;
    }

    .bottom-gripper {
      border-radius: 0 0 3px 3px;
    }
  }
`;

const ResizableDraggableDialog = ({
  anchorEl,
  title,
  children,
  open,
  draggable = false,
  dialogPaperStyle = {},
  defaultPosition,
  height,
  width,
  minHeight,
  minWidth,
  onStop,
  enableConfig = {},
}: Props) => {
  const draggableRef = useRef<HTMLDivElement>(null);
  const [dialogHeight, setDialogHeight] = useState(height);
  const initialPosition = defaultPosition ?? {
    top: anchorEl.clientTop,
    left: anchorEl.clientLeft,
  };

  useEffect(() => {
    if (!dialogHeight) {
      setDialogHeight(height);
    }
  }, [dialogHeight, height]);

  const handleResize = ((_e, _d, element) => {
    setDialogHeight(element.getBoundingClientRect().height);
  }) as ResizeCallback;

  return (
    <Portal>
      <Draggable
        handle="#draggable-dialog-content"
        bounds="#react-container"
        nodeRef={draggableRef}
        onStop={onStop}
      >
        <DialogPaper
          ref={draggableRef}
          sx={{
            position: 'fixed',
            zIndex: 1300,
            ...initialPosition,
            display: open ? 'block' : 'none',
            height: dialogHeight,
            width,
            ...dialogPaperStyle,
          }}
        >
          <Resizable
            bounds="window"
            enable={
              draggable
                ? {
                    top: false,
                    ...enableConfig,
                  }
                : false
            }
            minHeight={minHeight || height}
            minWidth={minWidth}
            defaultSize={{ width, height }}
            onResize={handleResize}
            handleStyles={{
              bottom: {
                zIndex: 2,
                height: 'fit-content',
                transform: 'translateY(-4px)',
              },
            }}
            handleComponent={{
              bottom: (
                <GrabArea>
                  <Gripper
                    GrabIcon={GrabAreaGrayIcon}
                    isBottom
                    className="resize bottom-gripper"
                  />
                </GrabArea>
              ),
            }}
          >
            <div style={{ height: '100%' }}>
              <GrabArea id="draggable-dialog-content">
                <Gripper GrabIcon={GrabAreaGrayIcon} className="drag gripper" />
                {title}
              </GrabArea>

              {children}
            </div>
          </Resizable>
        </DialogPaper>
      </Draggable>
    </Portal>
  );
};

export default ResizableDraggableDialog;
